/**
 * Лента генераций одного кадра (прежний `_gens.html.j2`).
 *
 * Плитка несёт всё, что показывает крупный просмотр: модель, качество, размер,
 * цену, время — второй источник тех же данных разошёлся бы с лентой ровно там,
 * где по ним сравнивают кадры глазами. Порядок — новые слева (так отдаёт
 * сервер: `ORDER BY g.id DESC`), и это написано в подписи, а не подразумевается.
 */

import clsx from "clsx";
import { ArrowUpFromLine, Star } from "lucide-react";

import { mediaUrl } from "@/lib/media";
import type { Gen, GensResult } from "@/lib/project";
import { useGenNote, useGens, usd, useVariantActions } from "@/lib/project";

import { ErrorLine } from "./ui";

export function GensFeed({
  pid,
  sid,
  seed,
  openVariant,
  onOpen,
}: {
  pid: number;
  sid: number;
  seed?: { data: GensResult; at: number } | undefined;
  openVariant: number | null;
  onOpen: (variantId: number) => void;
}) {
  const { data } = useGens(pid, sid, seed);
  const note = useGenNote(pid, sid);
  const actions = useVariantActions(pid, sid);
  const gens = data?.gens ?? [];
  const variants = gens.filter((g) => g.variant_id !== null).length;
  const picked = gens.filter((g) => g.pick === "picked").length;

  return (
    <div className="mt-2 grid gap-1.5" data-annot="лента генераций">
      <ErrorLine text={note} />
      {gens.length === 0 ? (
        <div className="text-[11.5px] text-fg-faint">генераций ещё не было</div>
      ) : (
        <>
          <div className="text-[11.5px] text-fg-faint">
            генерации — {variants}
            {picked ? ` · выбрано ${picked}` : ""} · новые слева
            {(data?.pending ?? 0) > 0 ? ` · в очереди и в работе по проекту: ${data?.pending}` : ""}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {gens.map((g) => (
              <GenTile
                key={`${g.generation_id}-${g.variant_id ?? "x"}`}
                g={g}
                open={g.variant_id !== null && g.variant_id === openVariant}
                onOpen={onOpen}
                onPick={actions.pick}
                onReshoot={actions.reshoot}
                picking={g.variant_id !== null && actions.picking === g.variant_id}
                reshooting={g.variant_id !== null && actions.reshooting === g.variant_id}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function GenTile({
  g,
  open,
  onOpen,
  onPick,
  onReshoot,
  picking,
  reshooting,
}: {
  g: Gen;
  open: boolean;
  onOpen: (variantId: number) => void;
  onPick: (variantId: number) => void;
  onReshoot: (variantId: number) => void;
  picking: boolean;
  reshooting: boolean;
}) {
  const isPicked = g.pick === "picked";
  const waiting = g.status === "queued" || g.status === "running";
  const vid = g.variant_id;

  return (
    <div
      className={clsx(
        "relative w-[78px] flex-none rounded-[var(--radius-ui)] border bg-bg p-1",
        isPicked ? "border-accent ring-1 ring-accent" : "border-border",
        open && "outline-2 outline-offset-1 outline-accent-text",
      )}
      title={`${g.generation_model} · ${g.quality} · ${g.size}`}
    >
      {vid !== null && g.asset_path ? (
        <>
          {/* Ссылка остаётся ссылкой: обычный щелчок открывает крупный
              просмотр, Ctrl-щелчок и средняя кнопка — оригинал вкладкой. */}
          <a
            href={mediaUrl(g.asset_path)}
            target="_blank"
            rel="noopener"
            onClick={(e) => {
              if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return;
              e.preventDefault();
              onOpen(vid);
            }}
          >
            <img
              src={mediaUrl(g.asset_path)}
              alt={`${g.short_model} ${g.quality}`}
              loading="lazy"
              className="aspect-[9/16] w-full rounded-[4px] object-cover"
            />
          </a>
          <button
            type="button"
            title={isPicked ? "снять отметку" : "отметить удачным"}
            disabled={picking}
            onClick={() => onPick(vid)}
            className={clsx(
              "absolute top-1.5 right-1.5 grid size-6 place-items-center rounded-full bg-bg/80 backdrop-blur hover:bg-bg",
              isPicked ? "text-accent" : "text-fg-muted",
            )}
          >
            <Star size={13} fill={isPicked ? "currentColor" : "none"} />
          </button>
          {/* Пересъёмка на КАЖДОЙ плитке, а не только на отмеченной: выбор
              глазами и решение «за этот платим по полной» — одно движение. */}
          <button
            type="button"
            title="переснять этот кадр в качестве 1088×1920"
            disabled={reshooting}
            onClick={() => onReshoot(vid)}
            className="absolute top-1.5 left-1.5 grid size-6 place-items-center rounded-full bg-bg/80 text-fg-muted backdrop-blur hover:bg-bg hover:text-accent disabled:opacity-50"
          >
            <ArrowUpFromLine size={12} />
          </button>
        </>
      ) : waiting ? (
        <div
          className="grid aspect-[9/16] w-full place-items-center rounded-[4px] bg-warn-bg text-[18px] text-warn"
          title={g.status === "queued" ? "в очереди" : "генерируется"}
        >
          <span className={g.status === "running" ? "animate-pulse" : ""}>{g.status === "queued" ? "…" : "◐"}</span>
        </div>
      ) : g.status === "failed" ? (
        <div
          className="grid aspect-[9/16] w-full place-items-center rounded-[4px] bg-err-bg p-1 text-center text-err"
          title={g.generation_error ?? "ошибка"}
        >
          <div>
            <div className="text-[18px] font-semibold">!</div>
            <div className="line-clamp-4 text-[9.5px] leading-tight break-words">{g.generation_error ?? "ошибка"}</div>
          </div>
        </div>
      ) : (
        <div className="grid aspect-[9/16] w-full place-items-center rounded-[4px] bg-bg-elevated text-[11px] text-fg-faint">
          {g.status}
        </div>
      )}
      {/* Модель и цена — в две строки: в одну на плашке шириной с
          миниатюру цена обрезалась бы многоточием, а ради неё подпись и стоит. */}
      <div className="mt-1 text-[10.5px] leading-tight text-fg-faint">
        <div className="truncate">
          {g.short_model} · {g.quality}
        </div>
        {g.cost_each !== null ? (
          <b className="tnum font-medium text-fg-muted">{usd(g.cost_each)}</b>
        ) : waiting ? (
          <b className="tnum font-medium text-warn">~{usd(g.cost_each_estimate ?? 0)}</b>
        ) : null}
      </div>
    </div>
  );
}
